import { NO_PROXY } from '@hit-game/shared/constants';
import { AccountRunStatus } from '@hit-game/shared/enums';
import type { IAccountDataResponse } from '@hit-game/shared/types';
import { Shield, UserMinus } from 'lucide-react';

import { MEMBER_COLORS } from './group.constants';
import type { DraftMember } from './group.form';

const CELL_CLASS = 'px-4 py-3 align-middle';

const CHIP_CLASS = 'inline-block rounded px-2 py-0.5 text-[10px] font-bold';

/** Trạng thái sống của tài khoản — chỉ để nhìn, đổi ở Sảnh và Kho tài khoản. */
function statusChip(account: IAccountDataResponse | undefined): { text: string; class: string } {
  if (!account) return { text: 'ĐÃ XOÁ', class: 'bg-danger-bg text-danger' };
  if (account.status === AccountRunStatus.RUNNING) {
    return { text: 'ĐANG CHẠY', class: 'bg-success-bg text-success' };
  }
  return account.isLoggedIn
    ? { text: 'ĐÃ ĐĂNG NHẬP', class: 'bg-accent-soft text-accent' }
    : { text: 'CHƯA ĐĂNG NHẬP', class: 'bg-panel-hover text-ink-dim' };
}

interface GroupMemberRowProps {
  draft: DraftMember;
  /** Thứ tự trong nhóm — quyết định màu chấm để đối chiếu với thẻ ngoài sảnh. */
  index: number;
  /** Dữ liệu sống của tài khoản; `undefined` = đã xoá nhưng chưa gỡ khỏi nhóm. */
  account: IAccountDataResponse | undefined;
  onRemove: () => void;
}

/**
 * Một dòng thành viên — CHỈ ĐỌC, trừ nút gỡ khỏi nhóm.
 *
 * KHÔNG còn cột phe và cột mệnh giá: phe được random lại mỗi ván nên không có
 * giá trị cố định nào để hiện, còn tiền thì khai một lần cho cả nhóm trong hộp
 * thoại "Sửa nhóm".
 */
export function GroupMemberRow({ draft, index, account, onRemove }: GroupMemberRowProps) {
  const status = statusChip(account);
  const proxy = account?.proxy ?? NO_PROXY;
  const running = account?.status === AccountRunStatus.RUNNING;

  return (
    <tr className="border-line hover:bg-panel-raised border-t transition-colors">
      <td className={CELL_CLASS}>
        <div className="flex items-center gap-2">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: MEMBER_COLORS[index % MEMBER_COLORS.length] }}
          />
          <span className="text-ink font-mono text-[12px] font-bold">
            {account?.username ?? 'Tài khoản đã xoá'}
          </span>
        </div>
      </td>

      <td className={CELL_CLASS}>
        <span className={`${CHIP_CLASS} ${status.class}`}>{status.text}</span>
      </td>

      <td className={CELL_CLASS}>
        <div className="flex items-center gap-1.5">
          <Shield size={12} className="text-ink-ghost shrink-0" />
          <span className="text-ink-muted font-mono text-[11px]" title={proxy}>
            {proxy === NO_PROXY ? NO_PROXY : proxy.split(':').slice(0, 2).join(':')}
          </span>
        </div>
      </td>

      <td className={`${CELL_CLASS} text-center`}>
        <button
          type="button"
          onClick={onRemove}
          // Gỡ giữa chừng là nhóm tụt xuống 3 người ngay trong ván đang chạy —
          // không chia phe được nữa, phần còn lại thành cược lệch.
          disabled={running}
          title={running ? 'Đang chạy — dừng ở Sảnh trước đã' : 'Gỡ khỏi nhóm'}
          className="text-ink-dim hover:bg-panel-hover hover:text-danger rounded p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
        >
          <UserMinus size={15} />
        </button>
      </td>
    </tr>
  );
}
